import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Upload, X, AlertTriangle, ShieldCheck, ImagePlus, Camera, Info, ChevronLeft, ChevronRight } from 'lucide-react';


export default function CreateListingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, tc } = useTranslation('createListing');
  const { t: tCond } = useTranslation('conditions');
  const { t: tSector } = useTranslation('sectors');
  const { t: tCat } = useTranslation('categories');

  const [form, setForm] = useState({
    title: '', description: '', price: '', quantity: 1,
    condition: '', sector: '', category: '', brand: '', reference: '',
    location: '', delivery: false, pickup: true, invoice: false, warranty: false,
  });
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [listingCount, setListingCount] = useState(0);
  const [sectorsData, setSectorsData] = useState({});

  useEffect(() => {
    api.get('/sectors').then(r => setSectorsData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user && user.verification_status !== 'verified') {
      api.get('/listings/my').then(r => {
        const data = r.data.listings || r.data;
        const count = Array.isArray(data) ? data.length : 0;
        setListingCount(count);
        if (count >= 2) setLimitReached(true);
      }).catch(() => {});
    }
  }, [user]);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const sectorKeys = Object.keys(sectorsData);
  const categories = form.sector && sectorsData[form.sector] ? sectorsData[form.sector].categories : [];

  const handleImageAdd = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 6) {
      toast.error('Maximum 6 photos');
      return;
    }
    const newImages = files.map(f => ({ file: f, preview: URL.createObjectURL(f) }));
    setImages(prev => [...prev, ...newImages]);
  };

  const removeImage = (idx) => {
    setImages(prev => prev.filter((_, i) => i !== idx));
  };

  const [dragIdx, setDragIdx] = useState(null);
  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (dropIdx) => {
    if (dragIdx === null || dragIdx === dropIdx) return;
    setImages(prev => {
      const arr = [...prev];
      const [moved] = arr.splice(dragIdx, 1);
      arr.splice(dropIdx, 0, moved);
      return arr;
    });
    setDragIdx(null);
  };
  const moveImage = (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= images.length) return;
    setImages(prev => {
      const arr = [...prev];
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      return arr;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.price || !form.sector || !form.category) {
      toast.error(t('fillRequired'));
      return;
    }
    setLoading(true);
    try {
      // 1. Upload images first
      let imageUrls = [];
      if (images.length > 0) {
        const formData = new FormData();
        images.forEach(img => formData.append('files', img.file));
        const uploadRes = await api.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        imageUrls = uploadRes.data.urls || [];
      }
      // 2. Create listing with JSON
      const res = await api.post('/listings', {
        ...form,
        price: parseFloat(form.price),
        images: imageUrls,
      });
      toast.success(t('publishSuccess'));
      navigate(`/annonces/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('publishError'));
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center" data-testid="create-listing-no-auth">
        <p className="text-lg font-semibold text-neutral-700 mb-4">{t('loginRequired')}</p>
        <Button className="bg-[#F25C05] hover:bg-[#d94f04] text-white" onClick={() => navigate('/inscription')}>
          {t('createAccountBtn')}
        </Button>
      </div>
    );
  }

  if (limitReached) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center" data-testid="create-listing-limit">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-100 mb-4">
          <AlertTriangle className="w-7 h-7 text-amber-600" />
        </div>
        <h1 className="font-heading text-2xl font-bold text-neutral-900 mb-3">{t('limitReached')}</h1>
        <p className="text-sm text-neutral-600 whitespace-pre-line mb-6">{t('limitMsg')}</p>
        <Button className="bg-[#1F3F72] text-white" onClick={() => navigate('/tableau-de-bord?tab=verification')}>
          <ShieldCheck className="w-4 h-4 mr-2" /> {t('verifyCompany')}
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="create-listing-page">
      <h1 className="font-heading text-2xl font-bold text-neutral-900 mb-2">{t('title')}</h1>

      {user.verification_status !== 'verified' && (
        <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-6" data-testid="listing-count-warning">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>{t('limitInfo').replace('{count}', listingCount)}</span>
          <Link to="/tableau-de-bord?tab=verification" className="font-semibold text-[#1F3F72] ml-auto shrink-0 hover:underline">
            {t('verify')}
          </Link>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* General Info */}
        <section className="bg-white border rounded-xl p-6 space-y-5" data-testid="create-general-section">
          <h2 className="font-heading text-lg font-semibold text-neutral-900">{t('generalInfo')}</h2>

          <div className="space-y-2">
            <Label>{t('adTitle')} *</Label>
            <Input value={form.title} onChange={(e) => update('title', e.target.value)} data-testid="listing-title-input" />
            <p className="text-xs text-neutral-500 leading-relaxed" data-testid="listing-title-help">{t('adTitleHelp')}</p>
          </div>

          <div className="space-y-2">
            <Label>{t('description')}</Label>
            <Textarea rows={4} placeholder={t('descPlaceholder')} value={form.description} onChange={(e) => update('description', e.target.value)} data-testid="listing-description-input" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
            <div className="space-y-2">
              <Label>{t('price')} *</Label>
              <Input type="number" step="0.01" min="0" value={form.price} onChange={(e) => update('price', e.target.value)} data-testid="listing-price-input" />
            </div>
            <div className="space-y-2">
              <Label>{t('quantity')}</Label>
              <Input type="number" min="1" value={form.quantity} onChange={(e) => update('quantity', parseInt(e.target.value) || 1)} data-testid="listing-quantity-input" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label>{t('condition')}</Label>
                <div className="relative group">
                  <Info className="w-4 h-4 text-neutral-400 cursor-help" />
                  <div className="absolute top-full left-0 mt-2 w-72 p-3 bg-neutral-900 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 sm:left-1/2 sm:-translate-x-1/2">
                    <p className="font-semibold mb-1.5">{t('conditionTooltipExamples')}</p>
                    <p className="mb-1">{t('conditionEx1')}</p>
                    <p className="mb-1">{t('conditionEx2')}</p>
                    <p className="mb-1">{t('conditionEx3')}</p>
                    <p className="mb-1">{t('conditionEx4')}</p>
                    <p className="mb-1">{t('conditionEx5')}</p>
                    <p>{t('conditionEx6')}</p>
                  </div>
                </div>
              </div>
              <Input placeholder={t('conditionPlaceholder')} value={form.condition} onChange={(e) => update('condition', e.target.value)} data-testid="listing-condition-input" />
            </div>
          </div>
        </section>

        {/* Classification */}
        <section className="bg-white border rounded-xl p-6 space-y-5" data-testid="create-classification-section">
          <h2 className="font-heading text-lg font-semibold text-neutral-900">{t('classification')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('sector')} *</Label>
              <select className="w-full rounded-md border border-neutral-200 px-3 py-2 text-sm bg-white" value={form.sector} onChange={(e) => { update('sector', e.target.value); update('category', ''); }} data-testid="listing-sector-select">
                <option value="">{t('sectorPlaceholder')}</option>
                {sectorKeys.map(s => <option key={s} value={s}>{tSector(s)}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label>{t('categoryLabel')} *</Label>
              <select className="w-full rounded-md border border-neutral-200 px-3 py-2 text-sm bg-white" value={form.category} onChange={(e) => update('category', e.target.value)} disabled={!form.sector} data-testid="listing-category-select">
                <option value="">{t('categoryPlaceholder')}</option>
                {categories.map(c => <option key={c} value={c}>{tCat(c)}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label>{t('brand')}</Label>
              <Input value={form.brand} onChange={(e) => update('brand', e.target.value)} data-testid="listing-brand-input" />
            </div>
            <div className="space-y-2">
              <Label>{t('reference')}</Label>
              <Input value={form.reference} onChange={(e) => update('reference', e.target.value)} data-testid="listing-reference-input" />
            </div>
          </div>
        </section>

        {/* Photos */}
        <section className="bg-white border rounded-xl p-6 space-y-5" data-testid="create-photos-section">
          <div>
            <h2 className="font-heading text-lg font-semibold text-neutral-900">{t('photos')}</h2>
            <p className="text-xs text-neutral-500 mt-0.5">{t('photosSubtitle')}</p>
          </div>
          {images.length > 1 && (
            <p className="text-xs text-neutral-400">Glissez les photos pour changer l'ordre. La premiere photo sera la photo principale.</p>
          )}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            {images.map((img, i) => (
              <div
                key={i}
                className={`relative aspect-square rounded-lg overflow-hidden border-2 cursor-grab active:cursor-grabbing transition-all ${dragIdx === i ? 'opacity-50 scale-95 border-[#F25C05]' : i === 0 ? 'border-[#F25C05] ring-2 ring-[#F25C05]/20' : 'border-neutral-200'}`}
                draggable
                onDragStart={() => handleDragStart(i)}
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(i)}
                data-testid={`image-slot-${i}`}
              >
                <img src={img.preview} alt="" className="w-full h-full object-cover" />
                {i === 0 && (
                  <span className="absolute top-1 left-1 bg-[#F25C05] text-white text-[9px] font-bold px-1.5 py-0.5 rounded">Principale</span>
                )}
                {i > 0 && (
                  <span className="absolute top-1 left-1 bg-black/50 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">{i + 1}</span>
                )}
                <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5">
                  <X className="w-3 h-3" />
                </button>
                {images.length > 1 && (
                  <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-1">
                    {i > 0 && (
                      <button type="button" onClick={() => moveImage(i, i - 1)} className="bg-black/60 hover:bg-black/80 text-white rounded-full p-0.5 transition-colors" data-testid={`move-left-${i}`}>
                        <ChevronLeft className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {i < images.length - 1 && (
                      <button type="button" onClick={() => moveImage(i, i + 1)} className="bg-black/60 hover:bg-black/80 text-white rounded-full p-0.5 transition-colors" data-testid={`move-right-${i}`}>
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
            {images.length < 6 && (
              <>
                {/* Mobile: Two buttons - Camera + Gallery side by side */}
                <div className="col-span-3 grid grid-cols-2 gap-3 sm:hidden">
                  <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#F25C05]/40 bg-orange-50/50 px-3 py-5 cursor-pointer hover:border-[#F25C05] transition-colors text-center" data-testid="listing-image-camera">
                    <Camera className="w-7 h-7 text-[#F25C05]" />
                    <div>
                      <span className="text-sm text-neutral-800 font-semibold block">Photo</span>
                      <span className="text-[10px] text-neutral-400 leading-tight">Ouvrir la camera</span>
                    </div>
                    <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleImageAdd} />
                  </label>
                  <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-neutral-300 px-3 py-5 cursor-pointer hover:border-[#1F3F72] transition-colors text-center" data-testid="listing-image-gallery">
                    <ImagePlus className="w-7 h-7 text-neutral-400" />
                    <div>
                      <span className="text-sm text-neutral-800 font-semibold block">Galerie</span>
                      <span className="text-[10px] text-neutral-400 leading-tight">Choisir une image</span>
                    </div>
                    <input type="file" accept="image/*" multiple className="hidden" onChange={handleImageAdd} />
                  </label>
                </div>
                {/* Desktop: Single upload button */}
                <label className="hidden sm:flex aspect-square rounded-lg border-2 border-dashed border-neutral-300 flex-col items-center justify-center cursor-pointer hover:border-[#1F3F72] hover:bg-neutral-50 transition-colors" data-testid="listing-image-upload">
                  <ImagePlus className="w-6 h-6 text-neutral-400" />
                  <span className="text-[10px] text-neutral-400 mt-1">{tc('add')}</span>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={handleImageAdd} />
                </label>
              </>
            )}
          </div>
        </section>

        {/* Location & Options */}
        <section className="bg-white border rounded-xl p-6 space-y-5" data-testid="create-options-section">
          <h2 className="font-heading text-lg font-semibold text-neutral-900">{t('locationOptions')}</h2>
          <div className="space-y-2">
            <Label>{t('location')}</Label>
            <Input placeholder={t('locationPlaceholder')} value={form.location} onChange={(e) => update('location', e.target.value)} data-testid="listing-location-input" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { key: 'delivery', label: t('deliveryAvailable') },
              { key: 'pickup', label: t('pickupOnSite') },
              { key: 'invoice', label: t('invoiceAvailable') },
              { key: 'warranty', label: t('warrantyLabel') },
            ].map(opt => (
              <label key={opt.key} className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer text-xs ${form[opt.key] ? 'border-[#1F3F72] bg-blue-50 text-[#1F3F72]' : 'border-neutral-200 text-neutral-600'}`}>
                <input type="checkbox" checked={form[opt.key]} onChange={(e) => update(opt.key, e.target.checked)} className="rounded" />
                {opt.label}
              </label>
            ))}
          </div>
        </section>

        {/* Submit */}
        <Button type="submit" className="w-full bg-[#F25C05] hover:bg-[#d94f04] text-white h-12 text-base" disabled={loading} data-testid="listing-submit-btn">
          {loading ? t('publishing') : <><Upload className="w-4 h-4 mr-2" /> {t('publishBtn')}</>}
        </Button>
      </form>
    </div>
  );
}
